/**
 * StammdatenStep — Modul 00 für die Mandanten-Self-Erfassung der
 * personen- und unternehmensbezogenen Stammdaten.
 *
 * Routing:
 *   - /mandant/case/:id/wizard/stammdaten   (Mandant)
 *   - /case/:id/wizard/stammdaten           (Berater — wird durch denselben
 *                                            Step bedient; alle Felder
 *                                            schreibbar via cases-Policy 0018)
 *
 * Datenfluss:
 *   1. Beim Mount: getCase() laden, in lokale Draft mappen.
 *   2. Auto-Save bei Blur jedes Feldes — debounced via 600 ms Idle. Wir
 *      könnten das per Hook abstrahieren, aber im Sinne der Pure-Function-
 *      Tests-Strategie halten wir die DB-Roundtrips in einer kompakten
 *      lokalen Funktion.
 *   3. Save-Status-Anzeige im SaveIndicator-Slot des WizardStep.
 *   4. Wenn der Mandant beim allerersten Mount keine Stammdaten hat,
 *      zeigen wir den `firstTimeIntro`-Hinweis.
 *
 * UX-Prinzip: Die erste Eingabe ist die wichtigste — wir zeigen den
 * Editorial-Header NUR mit „Stammdaten", ohne Modul-Akzentfarbe. Modul 00
 * hat eine neutrale Stein-Farbe (analog Triage in dimensions.ts).
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { de } from '../../lib/i18n/de';
import { WizardLayout } from '../../components/wizard/WizardLayout';
import { useAuthOrSession } from '../../lib/authOrSession';
import { getCase } from '../../lib/supabaseCases';
import { updateCaseStammdaten } from '../../lib/stammdaten/supabaseStammdaten';
import {
  caseToDraft,
  emptyStammdatenDraft,
  validateStammdaten,
  type StammdatenDraft,
  type StammdatenError,
  type StammdatenFieldKey,
  type StammdatenFieldValidation,
} from '../../lib/stammdaten/stammdatenValidation';
import {
  BRANCH_OPTIONS,
  BRANCH_OTHER_VALUE,
  isPredefinedBranch,
} from '../../lib/stammdaten/branchOptions';
import { FieldHelp } from '../../components/forms/FieldHelp';
import { ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWizardAudience } from '../../components/wizard/WizardAudienceContext';
import { Button } from '../../components/ui/Button';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

interface SaveStatusInfo {
  readonly state: SaveState;
  readonly errorMessage?: string;
}

const AUTOSAVE_DEBOUNCE_MS = 600;

export function StammdatenStep() {
  const { id: caseId = '' } = useParams<{ id?: string }>();
  const audience = useWizardAudience();
  const navigate = useNavigate();
  const { isMock } = useAuthOrSession();

  // Lade-Status: loading | ready | not-found | error (transient)
  type LoadState =
    | { kind: 'loading' }
    | { kind: 'ready'; firstTime: boolean }
    | { kind: 'not-found' }
    | { kind: 'error' };
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);
  const [draft, setDraft] = useState<StammdatenDraft>(() => emptyStammdatenDraft());
  const [touched, setTouched] = useState<Set<StammdatenFieldKey>>(() => new Set());
  const [save, setSave] = useState<SaveStatusInfo>({ state: 'idle' });
  // Zeitstempel des tatsächlich erfolgreichen Speichervorgangs. Wird NICHT
  // bei jedem Render neu erzeugt (das wäre trügerisch), sondern nur im
  // Save-Success-Pfad gesetzt.
  const [savedAtIso, setSavedAtIso] = useState<string | null>(null);

  // Debounce-Timer für Auto-Save
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Letzte erfolgreich gespeicherte Draft (für „dirty"-Vergleich, vermeidet
  // unnötige UPDATE-Calls beim Re-Tippen identischer Werte)
  const lastSavedRef = useRef<StammdatenDraft | null>(null);

  // Mount: Case laden
  useEffect(() => {
    let cancelled = false;
    if (isMock) {
      // Demo-Pfad: Draft bleibt leer; firstTime = true
      setLoad({ kind: 'ready', firstTime: true });
      return;
    }
    setLoad({ kind: 'loading' });
    void (async () => {
      try {
        const c = await getCase(caseId);
        if (cancelled) return;
        if (!c) {
          setLoad({ kind: 'not-found' });
          return;
        }
        const next = caseToDraft({
          client_full_name: c.client_full_name,
          client_birthdate: c.client_birthdate,
          client_address: c.client_address,
          business_branch: c.business_branch,
          business_legal_form: c.business_legal_form,
          business_start_date: c.business_start_date,
          business_tax_id: c.business_tax_id,
          business_description: c.business_description,
        });
        setDraft(next);
        lastSavedRef.current = next;
        const anyValue =
          (c.client_full_name ?? '').length > 0 ||
          (c.business_branch ?? '').length > 0 ||
          (c.business_description ?? '').length > 0;
        setLoad({ kind: 'ready', firstTime: !anyValue });
      } catch (err) {
        // Transienter Fehler (Netzwerk) ≠ entzogener Zugang.
        console.warn('StammdatenStep: getCase failed', err);
        if (!cancelled) setLoad({ kind: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId, isMock, reloadKey]);

  // -------------------------------------------------------------------------
  // Auto-Save: schedule debounced UPDATE
  // -------------------------------------------------------------------------
  function scheduleSave(next: StammdatenDraft): void {
    if (isMock || load.kind !== 'ready') {
      // Im Demo-Modus simulieren wir „saved" sofort (UI-Feedback), aber
      // ohne DB-Roundtrip. Anwender bekommt visuelles Feedback.
      setSave({ state: 'saved' });
      setSavedAtIso(new Date().toISOString());
      return;
    }
    setSave({ state: 'saving' });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void (async () => {
        try {
          // Optimistisch: nur senden, wenn wirklich was geändert hat.
          // Wenn lastSavedRef === next inhaltlich, abbrechen.
          if (lastSavedRef.current !== null && shallowEqualDraft(lastSavedRef.current, next)) {
            setSave({ state: 'saved' });
            setSavedAtIso(new Date().toISOString());
            return;
          }
          await updateCaseStammdaten(caseId, next);
          lastSavedRef.current = next;
          setSave({ state: 'saved' });
          setSavedAtIso(new Date().toISOString());
        } catch (err) {
          setSave({
            state: 'error',
            errorMessage: err instanceof Error ? err.message : de.mandant.stammdaten.saveError,
          });
        }
      })();
    }, AUTOSAVE_DEBOUNCE_MS);
  }

  function setField<K extends StammdatenFieldKey>(key: K, value: string): void {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      scheduleSave(next);
      return next;
    });
  }

  function markTouched(key: StammdatenFieldKey): void {
    setTouched((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }

  // Cleanup
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const validation = useMemo(() => validateStammdaten(draft), [draft]);

  const isLastEditedAt = save.state === 'saved' ? savedAtIso : null;

  if (load.kind === 'loading') {
    return (
      <WizardLayout audience={audience}>
        <p className="font-serif italic text-stone-500">Wird geladen …</p>
      </WizardLayout>
    );
  }

  if (load.kind === 'not-found') {
    return (
      <WizardLayout audience={audience}>
        <div className="rounded-sm border border-stone-200 bg-white px-6 py-8 md:px-10">
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            {de.mandant.stammdaten.sectionLabel}
          </p>
          <h1 className="mt-3 font-serif text-3xl font-black tracking-tight text-stone-900 md:text-5xl">
            {de.mandant.home.notFoundTitle}
          </h1>
          <p className="mt-3 font-serif text-base italic text-stone-500">
            {de.mandant.home.notFoundBody}
          </p>
        </div>
      </WizardLayout>
    );
  }

  if (load.kind === 'error') {
    return (
      <WizardLayout audience={audience}>
        <div className="rounded-sm border border-stone-200 bg-white px-6 py-8 md:px-10">
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            {de.mandant.stammdaten.sectionLabel}
          </p>
          <h1 className="mt-3 font-serif text-3xl font-black tracking-tight text-stone-900 md:text-5xl">
            {de.mandant.home.loadErrorTitle}
          </h1>
          <p className="mt-3 font-serif text-base italic text-stone-500">
            {de.mandant.home.loadErrorBody}
          </p>
          <Button
            type="button"
            onClick={() => {
              setReloadKey((k) => k + 1);
            }}
            className="mt-6"
          >
            {de.mandant.home.loadErrorRetry} →
          </Button>
        </div>
      </WizardLayout>
    );
  }

  return (
    <WizardLayout audience={audience}>
      <section
        aria-labelledby="stammdaten-step-title"
        className="rounded-sm border border-stone-200 bg-white"
      >
        <header className="relative flex flex-col gap-3 border-b border-stone-200 px-6 pb-8 pt-10 md:px-10 md:pb-10 md:pt-14">
          <div
            aria-hidden="true"
            className="absolute right-6 top-6 h-12 w-12 rounded-sm bg-stone-300 opacity-40 md:right-10 md:top-10 md:h-16 md:w-16"
          />
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            {de.mandant.stammdaten.sectionLabel}
          </p>
          <h1
            id="stammdaten-step-title"
            className="font-serif text-3xl font-black leading-none tracking-tight text-stone-900 md:text-5xl"
          >
            {de.mandant.stammdaten.title}
          </h1>
          <p className="font-serif text-base italic text-stone-500 md:text-lg">
            {de.mandant.stammdaten.moduleSubtitle}
          </p>
        </header>

        <div className="px-6 py-8 md:px-10 md:py-10">
          {load.firstTime && (
            <p className="mb-8 rounded-sm border-l-2 border-stone-300 bg-stone-50 px-4 py-3 font-serif text-sm leading-relaxed text-stone-700 md:px-5 md:py-4">
              {de.mandant.stammdaten.firstTimeIntro}
            </p>
          )}

          {isMock && (
            <p className="mb-6 rounded-sm border border-amber-200 bg-amber-50 px-4 py-2 font-sans text-xs font-medium uppercase tracking-widest text-amber-900">
              {de.mandant.stammdaten.mockHint}
            </p>
          )}

          {/* Personenbezogene Daten */}
          <fieldset className="mb-12">
            <legend className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
              {de.mandant.stammdaten.sectionPersonal}
            </legend>
            <div className="mt-5 space-y-7">
              <TextField
                label={de.mandant.stammdaten.fullNameLabel}
                value={draft.clientFullName}
                onChange={(v) => {
                  setField('clientFullName', v);
                }}
                onBlur={() => {
                  markTouched('clientFullName');
                }}
                required
                placeholder={de.mandant.stammdaten.fullNamePlaceholder}
                helpKey="stammdaten.fullName"
                validation={
                  touched.has('clientFullName') ? validation.fields.clientFullName : { ok: true }
                }
              />
              <DateField
                label={de.mandant.stammdaten.birthdateLabel}
                value={draft.clientBirthdate}
                onChange={(v) => {
                  setField('clientBirthdate', v);
                }}
                onBlur={() => {
                  markTouched('clientBirthdate');
                }}
                required
                helpKey="stammdaten.birthdate"
                validation={
                  touched.has('clientBirthdate') ? validation.fields.clientBirthdate : { ok: true }
                }
              />
              <TextareaField
                label={de.mandant.stammdaten.addressLabel}
                value={draft.clientAddress}
                onChange={(v) => {
                  setField('clientAddress', v);
                }}
                onBlur={() => {
                  markTouched('clientAddress');
                }}
                required
                placeholder={de.mandant.stammdaten.addressPlaceholder}
                rows={3}
                helpKey="stammdaten.address"
                validation={
                  touched.has('clientAddress') ? validation.fields.clientAddress : { ok: true }
                }
              />
            </div>
          </fieldset>

          {/* Geschäftsdaten */}
          <fieldset>
            <legend className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
              {de.mandant.stammdaten.sectionBusiness}
            </legend>
            <div className="mt-5 space-y-7">
              <BranchField
                value={draft.businessBranch}
                onChange={(v) => {
                  setField('businessBranch', v);
                }}
                onBlur={() => {
                  markTouched('businessBranch');
                }}
                validation={
                  touched.has('businessBranch') ? validation.fields.businessBranch : { ok: true }
                }
              />
              <SelectField
                label={de.mandant.stammdaten.legalFormLabel}
                value={draft.businessLegalForm}
                onChange={(v) => {
                  setField('businessLegalForm', v);
                }}
                onBlur={() => {
                  markTouched('businessLegalForm');
                }}
                required
                helpKey="stammdaten.legalForm"
                placeholder={de.mandant.stammdaten.legalFormPlaceholder}
                options={[
                  {
                    value: 'Einzelunternehmen',
                    label: de.mandant.stammdaten.legalFormOptions.einzelunternehmen,
                  },
                  { value: 'GbR', label: de.mandant.stammdaten.legalFormOptions.gbr },
                  { value: 'UG', label: de.mandant.stammdaten.legalFormOptions.ug },
                  { value: 'GmbH', label: de.mandant.stammdaten.legalFormOptions.gmbh },
                  { value: 'PartG', label: de.mandant.stammdaten.legalFormOptions.partg },
                  { value: 'Andere', label: de.mandant.stammdaten.legalFormOptions.andere },
                ]}
                validation={
                  touched.has('businessLegalForm')
                    ? validation.fields.businessLegalForm
                    : { ok: true }
                }
              />
              <DateField
                label={de.mandant.stammdaten.startDateLabel}
                value={draft.businessStartDate}
                onChange={(v) => {
                  setField('businessStartDate', v);
                }}
                onBlur={() => {
                  markTouched('businessStartDate');
                }}
                required
                helpKey="stammdaten.startDate"
                validation={
                  touched.has('businessStartDate')
                    ? validation.fields.businessStartDate
                    : { ok: true }
                }
              />
              <TextField
                label={de.mandant.stammdaten.taxIdLabel}
                value={draft.businessTaxId}
                onChange={(v) => {
                  setField('businessTaxId', v);
                }}
                onBlur={() => {
                  markTouched('businessTaxId');
                }}
                placeholder={de.mandant.stammdaten.taxIdPlaceholder}
                hint={de.mandant.stammdaten.taxIdHint}
                helpKey="stammdaten.taxId"
                validation={
                  touched.has('businessTaxId') ? validation.fields.businessTaxId : { ok: true }
                }
              />
              <TextareaField
                label={de.mandant.stammdaten.descriptionLabel}
                value={draft.businessDescription}
                onChange={(v) => {
                  setField('businessDescription', v);
                }}
                onBlur={() => {
                  markTouched('businessDescription');
                }}
                required
                placeholder={de.mandant.stammdaten.descriptionPlaceholder}
                rows={4}
                hint={de.mandant.stammdaten.descriptionHint}
                helpKey="stammdaten.description"
                validation={
                  touched.has('businessDescription')
                    ? validation.fields.businessDescription
                    : { ok: true }
                }
                charCount={{
                  current: draft.businessDescription.length,
                  template: de.mandant.stammdaten.descriptionCharCount,
                }}
              />
            </div>
          </fieldset>
        </div>

        {/* Footer mit Save-Indikator + Zurück */}
        <footer className="flex flex-col items-stretch gap-4 border-t border-stone-200 px-6 py-6 md:flex-row md:items-center md:justify-between md:px-10">
          <div className="font-sans text-xs font-medium uppercase tracking-widest">
            {save.state === 'saving' && (
              <span className="text-stone-500">{de.mandant.stammdaten.saving}</span>
            )}
            {save.state === 'saved' && (
              <span className="text-emerald-700">
                {de.mandant.stammdaten.saveSuccess}
                {isLastEditedAt && (
                  <span className="ml-2 text-stone-500">
                    ·{' '}
                    {new Date(isLastEditedAt).toLocaleTimeString('de-DE', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                )}
              </span>
            )}
            {save.state === 'error' && (
              <span className="text-rose-700">
                {save.errorMessage ?? de.mandant.stammdaten.saveError}
              </span>
            )}
            {save.state === 'idle' && (
              <span className="text-stone-300">{de.wizard.autosaveExplain}</span>
            )}
          </div>
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-3">
            <button
              type="button"
              onClick={() => {
                const home =
                  audience === 'mandant' ? `/mandant/case/${caseId}` : `/case/${caseId}/hub`;
                void navigate(home);
              }}
              className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-stone-200 bg-white px-5 py-3 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 transition-colors hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              <ChevronLeft size={14} aria-hidden="true" /> Zur Übersicht
            </button>
          </div>
        </footer>
      </section>

      {validation.missingFields.length > 0 && (
        <p className="mt-6 font-serif text-xs italic text-stone-500">
          Noch unvollständig: {validation.missingFields.length} Pflichtfeld(er).
        </p>
      )}
    </WizardLayout>
  );
}

// -----------------------------------------------------------------------------
// Felder
// -----------------------------------------------------------------------------

interface BaseFieldProps {
  readonly label: string;
  readonly required?: boolean;
  readonly hint?: string;
  readonly helpKey?: string;
  readonly validation?: StammdatenFieldValidation;
  readonly onBlur?: () => void;
}

function FieldFrame({
  label,
  required,
  hint,
  helpKey,
  validation,
  children,
  charCount,
}: BaseFieldProps & {
  readonly children: React.ReactNode;
  readonly charCount?: { current: number; template: string };
}) {
  const errorMsg = validation && !validation.ok ? errorMessageFor(validation.error) : null;
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <label className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {label}{' '}
          {required && (
            <span className="text-rose-700" aria-hidden="true">
              *
            </span>
          )}
        </label>
        {helpKey && <FieldHelp pathKey={helpKey as Parameters<typeof FieldHelp>[0]['pathKey']} />}
      </div>
      {hint && <p className="mt-1 font-serif text-xs italic text-stone-500">{hint}</p>}
      <div className="mt-2">{children}</div>
      {errorMsg && <p className="mt-1 font-sans text-xs text-rose-700">{errorMsg}</p>}
      {charCount && (
        <p className="mt-1 text-right font-mono text-[10px] text-stone-500">
          {charCount.template.replace('{count}', String(charCount.current))}
        </p>
      )}
    </div>
  );
}

function TextField(
  props: BaseFieldProps & {
    readonly value: string;
    readonly onChange: (v: string) => void;
    readonly placeholder?: string;
  },
) {
  const id = useId();
  const errorState = props.validation && !props.validation.ok;
  return (
    <FieldFrame {...props}>
      <input
        id={id}
        type="text"
        value={props.value}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
        onBlur={props.onBlur}
        placeholder={props.placeholder}
        aria-invalid={errorState ? true : undefined}
        className={`w-full rounded-sm border px-4 py-3 font-serif text-base text-stone-900 placeholder:text-stone-400 focus:border-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2 ${errorState ? 'border-rose-400 bg-rose-50' : 'border-stone-300 bg-white'}`}
      />
    </FieldFrame>
  );
}

function DateField(
  props: BaseFieldProps & {
    readonly value: string;
    readonly onChange: (v: string) => void;
  },
) {
  const id = useId();
  const errorState = props.validation && !props.validation.ok;
  return (
    <FieldFrame {...props}>
      <input
        id={id}
        type="date"
        value={props.value}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
        onBlur={props.onBlur}
        max={new Date().toISOString().slice(0, 10)}
        aria-invalid={errorState ? true : undefined}
        className={`w-full rounded-sm border px-4 py-3 font-serif text-base text-stone-900 focus:border-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2 ${errorState ? 'border-rose-400 bg-rose-50' : 'border-stone-300 bg-white'}`}
      />
    </FieldFrame>
  );
}

function TextareaField(
  props: BaseFieldProps & {
    readonly value: string;
    readonly onChange: (v: string) => void;
    readonly placeholder?: string;
    readonly rows?: number;
    readonly charCount?: { current: number; template: string };
  },
) {
  const id = useId();
  const errorState = props.validation && !props.validation.ok;
  // exactOptionalPropertyTypes: nicht `charCount={undefined}` durchreichen.
  const frameProps = props.charCount ? { ...props, charCount: props.charCount } : props;
  return (
    <FieldFrame {...frameProps}>
      <textarea
        id={id}
        value={props.value}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
        onBlur={props.onBlur}
        placeholder={props.placeholder}
        rows={props.rows ?? 3}
        aria-invalid={errorState ? true : undefined}
        className={`w-full rounded-sm border px-4 py-3 font-serif text-base leading-relaxed text-stone-900 placeholder:text-stone-400 focus:border-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2 ${errorState ? 'border-rose-400 bg-rose-50' : 'border-stone-300 bg-white'}`}
      />
    </FieldFrame>
  );
}

function SelectField(
  props: BaseFieldProps & {
    readonly value: string;
    readonly onChange: (v: string) => void;
    readonly options: ReadonlyArray<{ value: string; label: string }>;
    readonly placeholder?: string;
  },
) {
  const id = useId();
  const errorState = props.validation && !props.validation.ok;
  return (
    <FieldFrame {...props}>
      <select
        id={id}
        value={props.value}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
        onBlur={props.onBlur}
        aria-invalid={errorState ? true : undefined}
        className={`w-full rounded-sm border px-4 py-3 font-serif text-base text-stone-900 focus:border-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2 ${errorState ? 'border-rose-400 bg-rose-50' : 'border-stone-300 bg-white'}`}
      >
        <option value="">{props.placeholder ?? '—'}</option>
        {props.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldFrame>
  );
}

/**
 * BranchField: kombinierter Dropdown + optionales Free-Text-Feld.
 *
 * Wenn der gespeicherte Wert keiner der vordefinierten Branchen entspricht,
 * zeigen wir das Dropdown mit „Andere" vorausgewählt UND das Free-Text-Feld
 * mit dem aktuellen Wert. Sobald der User eine vordefinierte Branche wählt,
 * verschwindet das Free-Text-Feld.
 */
function BranchField({
  value,
  onChange,
  onBlur,
  validation,
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  validation?: StammdatenFieldValidation;
}) {
  // Lokaler Zustand: ist der „Andere"-Modus aktiv?
  const isOther = value.trim().length > 0 && !isPredefinedBranch(value);
  const [otherActive, setOtherActive] = useState(isOther);
  // Sync, wenn der initial geladene Wert „andere" Charakter hat
  useEffect(() => {
    if (isOther && !otherActive) setOtherActive(true);
  }, [isOther, otherActive]);

  const dropdownValue = otherActive ? BRANCH_OTHER_VALUE : value;
  const errorState = validation && !validation.ok;

  return (
    <div>
      <div className="flex items-baseline gap-2">
        <label className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {de.mandant.stammdaten.branchLabel}{' '}
          <span className="text-rose-700" aria-hidden="true">
            *
          </span>
        </label>
        <FieldHelp pathKey="stammdaten.branch" />
      </div>
      <div className="mt-2">
        <select
          value={dropdownValue}
          onChange={(e) => {
            const v = e.target.value;
            if (v === BRANCH_OTHER_VALUE) {
              setOtherActive(true);
              // Wert bleibt bestehen, falls schon ein Free-Text vorlag
              if (isPredefinedBranch(value)) onChange('');
            } else {
              setOtherActive(false);
              onChange(v);
            }
          }}
          onBlur={onBlur}
          aria-invalid={errorState ? true : undefined}
          className={`w-full rounded-sm border px-4 py-3 font-serif text-base text-stone-900 focus:border-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2 ${errorState ? 'border-rose-400 bg-rose-50' : 'border-stone-300 bg-white'}`}
        >
          <option value="">{de.mandant.stammdaten.branchPlaceholder}</option>
          {BRANCH_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
          <option value={BRANCH_OTHER_VALUE}>{de.mandant.stammdaten.branchOther}</option>
        </select>
      </div>
      {otherActive && (
        <div className="mt-3">
          <label className="font-sans text-[11px] font-medium uppercase tracking-widest text-stone-500">
            {de.mandant.stammdaten.branchOtherInputLabel}
          </label>
          <input
            type="text"
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            onBlur={onBlur}
            placeholder={de.mandant.stammdaten.branchOtherPlaceholder}
            className="mt-1 w-full rounded-sm border border-stone-300 bg-white px-4 py-3 font-serif text-base text-stone-900 placeholder:text-stone-400 focus:border-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2"
          />
        </div>
      )}
      {errorState && (
        <p className="mt-1 font-sans text-xs text-rose-700">{errorMessageFor(validation?.error)}</p>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function shallowEqualDraft(a: StammdatenDraft, b: StammdatenDraft): boolean {
  return (
    a.clientFullName === b.clientFullName &&
    a.clientBirthdate === b.clientBirthdate &&
    a.clientAddress === b.clientAddress &&
    a.businessBranch === b.businessBranch &&
    a.businessLegalForm === b.businessLegalForm &&
    a.businessStartDate === b.businessStartDate &&
    a.businessTaxId === b.businessTaxId &&
    a.businessDescription === b.businessDescription
  );
}

function errorMessageFor(error: StammdatenError | undefined): string {
  switch (error) {
    case 'required':
      return de.mandant.stammdaten.validationRequired;
    case 'too-short':
      return de.mandant.stammdaten.validationDescriptionMin;
    case 'too-long':
      return de.mandant.stammdaten.validationDescriptionMax;
    case 'future-date':
      return de.mandant.stammdaten.validationFutureDate;
    case 'invalid-date':
      return de.mandant.stammdaten.validationFutureDate; // grobes Mapping
    default:
      return de.mandant.stammdaten.validationRequired;
  }
}
